import type { ExportDownload } from '@verantos-dev/core-utils';

const FALLBACK_ARCHIVE_NAME = 'analysis';
// Analysis names have no length limit; this keeps the saved path well inside
// Windows' 260-character limit once the Downloads folder is prepended.
const MAX_ARCHIVE_NAME_LENGTH = 100;

/** Cut at the last word break that fits, or hard-cut a name with no break. */
const truncateAtWord = (base: string): string => {
  if (base.length <= MAX_ARCHIVE_NAME_LENGTH) return base;
  const lastBreak = base.slice(0, MAX_ARCHIVE_NAME_LENGTH + 1).lastIndexOf('_');
  return lastBreak > 0 ? base.slice(0, lastBreak) : base.slice(0, MAX_ARCHIVE_NAME_LENGTH);
};

/**
 * The analysis name as a zip file name: whitespace runs become underscores,
 * characters that are not allowed in Windows or macOS file names are dropped,
 * and a name over 100 characters is cut at the last whole word.
 */
export const toAnalysisArchiveFilename = (analysisName: string): string => {
  const base = analysisName
    .trim()
    // eslint-disable-next-line no-control-regex
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, '')
    .replace(/\s+/g, '_');
  return `${truncateAtWord(base) || FALLBACK_ARCHIVE_NAME}.zip`;
};

interface AnalysisExportDownloadInput {
  projectId: string;
  analysisProfileId: string;
  analysisName: string;
}

/**
 * The analysis results download: the analytical dataset behind the analysis's
 * latest processed run, saved as `<analysis_name>.zip`.
 */
export const analysisExportDownload = ({
  projectId,
  analysisProfileId,
  analysisName,
}: AnalysisExportDownloadInput): ExportDownload => ({
  key: `analysis/${projectId}/${analysisProfileId}`,
  path:
    `/analyses/${encodeURIComponent(projectId)}` +
    `/analysis-profile/${encodeURIComponent(analysisProfileId)}/download`,
  filename: toAnalysisArchiveFilename(analysisName),
});
